import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { User, Moon, Sun } from 'lucide-react';
import LanguageToggle from '../UI/LanguageToggle';
import NavigationMenu from '../UI/NavigationMenu';
import UserDropdown from '../UI/UserDropdown';

const Header = ({ systemSettings, darkMode, user, onAuthClick, onProfileClick, onDarkModeToggle }) => {
  const { t } = useTranslation();

  // Use system settings or fallback to defaults
  const logoUrl = darkMode 
    ? (systemSettings?.branding?.darkLogoUrl || '/Scalovate_Logo_White.png')
    : (systemSettings?.branding?.logoUrl || '/Scalovate_Logo.png');
  const headerText = systemSettings?.branding?.headerText || t('header.tagline');
  
  // Get custom styling from CMS settings
  const headerBackgroundColor = systemSettings?.branding?.headerBackgroundColor || 'linear-gradient(to right, rgba(255, 255, 255, 0), rgba(200, 200, 200, 0.5))';
  const headerTextColor = systemSettings?.branding?.headerTextColor || '#1f2937';
  const headerTransparency = systemSettings?.branding?.headerTransparency || 90;
  
  // Convert hex color to rgba with transparency
  const hexToRgba = (hex, alpha) => {
    if (hex.startsWith('linear-gradient') || hex.startsWith('radial-gradient')) {
      return hex; // Return gradient as-is
    }
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha / 100})`;
  };

  // In dark mode, override admin colors with dark theme
  const headerStyle = darkMode ? {
    backgroundColor: 'rgba(17, 24, 39, 0.9)',
    color: '#ffffff',
    backdropFilter: 'blur(10px)',
    borderBottom: '1px solid rgba(55, 65, 81, 0.5)'
  } : {
    background: headerBackgroundColor.startsWith('linear-gradient') || headerBackgroundColor.startsWith('radial-gradient') 
      ? headerBackgroundColor 
      : hexToRgba(headerBackgroundColor, headerTransparency),
    color: headerTextColor,
    backdropFilter: 'blur(10px)',
    borderBottom: headerBackgroundColor.startsWith('linear-gradient') || headerBackgroundColor.startsWith('radial-gradient')
      ? '1px solid rgba(156, 163, 175, 0.3)'
      : `1px solid ${hexToRgba(headerBackgroundColor, 30)}`
  };

  return (
    <motion.header 
      className="sticky top-0 z-50 px-2 py-1 sm:px-4 sm:py-2"
      style={headerStyle}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <motion.img
            src={logoUrl}
            alt="Scalovate Logo"
            className="h-6 w-auto sm:h-7"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
          />
          <div className="hidden sm:block">
            <h1 className="text-sm sm:text-base font-semibold" style={{ color: darkMode ? '#ffffff' : headerTextColor }}>
              {headerText}
            </h1>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <NavigationMenu 
            navigationLinks={systemSettings?.branding?.navigationLinks} 
            darkMode={darkMode} 
          />
          <LanguageToggle darkMode={darkMode} />
          
          {/* Dark Mode Toggle */}
          {onDarkModeToggle && (
            <motion.button
              onClick={onDarkModeToggle}
              className={`p-2 rounded-lg transition-all duration-200 ${
                darkMode 
                  ? 'text-yellow-400 hover:text-yellow-300 hover:bg-white/10' 
                  : 'text-gray-600 hover:text-gray-800 hover:bg-black/10'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {darkMode ? <Sun size={16} /> : <Moon size={16} />}
            </motion.button>
          )}
          
          {/* User Authentication */}
          {user ? (
            <UserDropdown 
              user={user}
              darkMode={darkMode}
              onProfileClick={onProfileClick}
            />
          ) : (
            <motion.button
              onClick={onAuthClick}
              className={`p-2 rounded-lg transition-all duration-200 ${
                darkMode 
                  ? 'text-blue-400 hover:text-blue-300 hover:bg-blue-400/10' 
                  : 'text-blue-600 hover:text-blue-700 hover:bg-blue-600/10'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Sign In"
            >
              <User size={16} />
            </motion.button>
          )}
        </div>
      </div>
    </motion.header>
  );
};

export default Header; 
